import { useAppSelector } from 'app/hooks';
import { RootState } from 'app/store';

type RequestStatus = null | 'idle' | 'loading' | 'failed';

interface RequestStatusResult {
  status: RequestStatus;
  isIdle: boolean;
  isLoading: boolean;
  isFailed: boolean;
}

/**
 * Normaliza un selector de estado Redux en booleanos listos para usar en JSX.
 * Evita el patrón `status === 'loading'` disperso por los componentes.
 *
 * @param selector - Selector que devuelve el status del slice.
 *
 * @example
 * // Antes
 * const status = useAppSelector((state) => state.devices.status);
 * const isLoading = status === 'loading';
 *
 * // Después
 * const { isLoading, isFailed } = useRequestStatus((state) => state.devices.status);
 *
 * @example En JSX
 * const { isLoading, isFailed } = useRequestStatus((state) => state.patients.user.status);
 *
 * if (isLoading) return <Spinner />;
 * if (isFailed) return <ErrorMessage />;
 */
export const useRequestStatus = (selector: (state: RootState) => RequestStatus): RequestStatusResult => {
  const status = useAppSelector(selector);

  return {
    status,
    isIdle: status === 'idle' || status === null,
    isLoading: status === 'loading',
    isFailed: status === 'failed',
  };
};
